/**
 * Monotonic ULIDs (Phase 16C, design §6.6): 48-bit millisecond time + 80 random bits, Crockford
 * base32, 26 characters. Ids made in the same millisecond increment the random part, so they
 * always sort in creation order (op ids double as the outbox order and the Idempotency-Key).
 * No dependency: crypto.getRandomValues only.
 */

const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const TIME_LEN = 10;
const RANDOM_LEN = 16;

let lastTime = -1;
let lastRandom: number[] = [];

function randomDigits(): number[] {
  const bytes = new Uint8Array(RANDOM_LEN);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b % 32);
}

function encodeTime(ms: number): string {
  let out = '';
  let rest = ms;
  for (let i = 0; i < TIME_LEN; i++) {
    out = ALPHABET[rest % 32] + out;
    rest = Math.floor(rest / 32);
  }
  return out;
}

/** Adds one to a base32 digit array; throws on overflow (2^80 ids in one millisecond). */
function increment(digits: number[]): number[] {
  const next = [...digits];
  for (let i = next.length - 1; i >= 0; i--) {
    if (next[i] < 31) {
      next[i] += 1;
      return next;
    }
    next[i] = 0;
  }
  throw new Error('ULID random part overflowed');
}

export function ulid(now: number = Date.now()): string {
  // A clock that moves backwards keeps the last time so ids stay sorted on this device
  const time = Math.max(now, lastTime);
  lastRandom = time === lastTime ? increment(lastRandom) : randomDigits();
  lastTime = time;
  return encodeTime(time) + lastRandom.map((d) => ALPHABET[d]).join('');
}

const ULID_RE = /^[0-7][0-9A-HJKMNP-TV-Z]{25}$/;

export function isUlid(value: unknown): value is string {
  return typeof value === 'string' && ULID_RE.test(value);
}

/** The millisecond time inside a ULID. */
export function ulidTime(id: string): number {
  return id.slice(0, TIME_LEN).split('').reduce((acc, ch) => acc * 32 + ALPHABET.indexOf(ch), 0);
}
